import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

/**
 * Alert dialogs used by TripDialog:
 * - UK dates warning (missing date_left_uk / date_returned_uk)
 * - Overlapping trip detection
 */
const TripAlertDialogs = ({
  ukDatesWarning, handleUkDatesCancel, handleUkDatesConfirm,
  overlapAlert, handleOverlapCancel, handleOverlapConfirm,
  formatDateDisplay,
}) => {
  return (
    <>
      {/* UK Dates Warning Dialog */}
      <AlertDialog open={ukDatesWarning.open} onOpenChange={(open) => !open && handleUkDatesCancel()}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Missing UK Travel Dates
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-slate-300">
                <p className="mb-3">
                  The following UK travel date(s) have not been provided:
                </p>
                <div className="space-y-3 mb-4">
                  {ukDatesWarning.missingFields.map((item, idx) => (
                    <div key={item.field || `field-${idx}`} className="bg-slate-900 p-3 rounded-lg border border-amber-500/30">
                      <p className="text-amber-400 font-medium">{item.field}</p>
                      <p className="text-sm text-slate-400 mt-1">{item.suggestion}</p>
                      <p className="text-sm text-slate-500 mt-2">
                        If you continue, <span className="text-amber-400 font-medium">{item.fallback}</span> (vessel date) will be used.
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-400">
                  Would you like to go back and enter the correct dates, or continue with the vessel dates?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleUkDatesCancel}
              className="bg-amber-600 text-white border-amber-600 hover:bg-amber-700"
            >
              Go Back & Enter Dates
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUkDatesConfirm}
              className="bg-slate-600 hover:bg-slate-700 text-white"
            >
              Use Vessel Dates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Overlap Alert Dialog */}
      <AlertDialog open={overlapAlert.open} onOpenChange={(open) => !open && handleOverlapCancel()}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Overlapping Trip Detected
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              <p className="mb-3">
                The trip you are adding overlaps with the following existing trip(s):
              </p>
              <div className="space-y-2 mb-3">
                {overlapAlert.overlappingTrips.map((t, idx) => (
                  <div key={t.id || `overlap-${idx}`} className="bg-slate-900 p-3 rounded-lg border border-amber-500/30">
                    <p className="text-white font-medium">{t.vessel_name || t.hotel_name || t.course_name || 'Trip'}</p>
                    <p className="text-sm text-slate-400">
                      {formatDateDisplay(t.date_left_uk || t.date_joined_vessel)} → {formatDateDisplay(t.date_returned_uk || t.date_left_vessel)}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-slate-400">
                Do you want to proceed and add this trip anyway?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleOverlapCancel}
              className="bg-slate-700 text-slate-300 border-slate-600 hover:bg-slate-600"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleOverlapConfirm}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Add Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TripAlertDialogs;
